import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import moment from "moment";
import { FC, useState, useEffect, Dispatch } from "react";
import EllipsisTooltip from "../../../../assets/components/EllipsisTooltip/EllipsisTooltip";
import Checkbox from "../../../../assets/components/Checkbox/Checkbox";
import CopyToClipboard from "../../../../assets/components/CopyToClipboard/CopyToClipboard";
import RemoveButton from "../../../../assets/components/RemoveButton/RemoveButton";
import Table from "../../../../assets/components/Table/Table";
import { hasPermission } from "../../../../assets/helpers/helpers";
import { useAppDispatch, useAppSelector } from "../../../../assets/hooks/hooks";
import { useNotification } from "../../../../assets/hooks/useNotification";
import { Permissions } from "../../../../models/models";
import { actionText } from "../../../../text";
import { LicenseKeysModel } from "../../model";
import {
    MigratedLicenseKeysData,
    renderSubComponentOneLicenseMigratedTable,
} from "../model";
import { migratedLicenseSliceActions } from "../../../../redux/migrated-license-slice";
import ImageSprite from "../../../../assets/components/ImageSprite/ImageSprite";
import CheckImg from "./Img/check.svg";
import { useDeleteMigratedLicensesMutation } from "../../../../redux/RTKQuery/api";
import ButtonArrow from "../../../../assets/components/ButtonArrow/ButtonArrow";
import { SetStateAction } from "../../../../models/models.helpers";
import { SearchBarDataHandler } from "../../../../assets/components/SearchBar/SearchBar";
import LicenseKeysMigratedSubRow from "../LicenseKeysMigratedSubRow/LicenseKeysMigratedSubRow";
import ArrowSprite from "../../../../assets/components/Table/Img/arrow_sprite.svg";
import TableColumnVisibilityMenu from "../../../../assets/components/Table/TableColumnVisibilityMenu";

type Removed = {
    id: number;
};

const MigratedStatus: FC<{ isMigrated: boolean }> = ({ isMigrated }) => {
    return (
        <div className="migrated_status" style={{ width: 24, height: 24 }}>
            <ImageSprite
                iconSprite={`${CheckImg}${
                    isMigrated ? "#checked" : "#unchecked"
                }`}
            />
        </div>
    );
};

const RemovedPost: FC<Removed> = ({ id }) => {
    const [deleteProduct] = useDeleteMigratedLicensesMutation({
        fixedCacheKey: "delete_migrated_license",
    });
    const { AttentionMessageBox } = useNotification();

    const deleteProductHandler = () =>
        AttentionMessageBox({
            textContent: actionText("delete", "migrated product"),
        }).then((value) => {
            if (value === "yes") {
                deleteProduct([id]);
            }
        });

    return (
        <RemoveButton
            position="top_center"
            img="top"
            onClick={() => deleteProductHandler()}
        />
    );
};

type RowItemCheckboxProps = {
    original: MigratedLicenseKeysData;
    accessorKey: string;
};

const RowItemCheckbox: FC<RowItemCheckboxProps> = ({
    original,
    accessorKey,
}) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.initializeApp);

    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.LICENSE_DELETE
    );

    const candidates = useAppSelector(
        (state) => state.migratedLicenses.candidatesForRemove
    );

    const idMigratedLicense = Number(original.id);
    const isCandidateForRemove = candidates.includes(idMigratedLicense);

    const [stateCheckbox, setStateCheckbox] =
        useState<boolean>(isCandidateForRemove);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStateCheckbox(!stateCheckbox);
        !stateCheckbox
            ? dispatch(
                  migratedLicenseSliceActions.addCandidate({
                      id: idMigratedLicense,
                  })
              )
            : dispatch(
                  migratedLicenseSliceActions.removeCandidate(idMigratedLicense)
              );
        return event;
    };

    useEffect(() => {
        setStateCheckbox(isCandidateForRemove);
    }, [stateCheckbox, candidates, isCandidateForRemove]);

    return (
        <Checkbox
            id={accessorKey}
            name={accessorKey}
            label={original.migratedProductName}
            checked={stateCheckbox ?? false}
            value={stateCheckbox.toString()}
            onChange={onChange}
            disabled={original.isMigrated || !isAccessPermission}
            useEllipsis
        />
    );
};

const renderSubComponent: renderSubComponentOneLicenseMigratedTable = (
    props
) => {
    return (
        <LicenseKeysMigratedSubRow
            {...props.row.original}
            key={props.row.original.globalOrderID}
        />
    );
};

interface Props {
    dataTable: MigratedLicenseKeysData[];
    onSort: Dispatch<SetStateAction<SearchBarDataHandler>>;
}

const LicenseKeysMigratedTable: FC<Props> = ({ dataTable, onSort }) => {
    const user = useAppSelector((state) => state.initializeApp);

    const [sortNextRebillDate, setSortNextRebillDate] = useState<
        boolean | undefined
    >(undefined);

    const defaultColumnVisibility: VisibilityState = {};

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultColumnVisibility,
    );

    const handleResetColumnVisibility = () => {
        setColumnVisibility(defaultColumnVisibility);
    };

    const handleSort = (state: boolean | undefined) => {
        let newState = state;
        if (state === undefined) newState = true;
        if (state === true) newState = false;
        if (state === false) newState = undefined;

        setSortNextRebillDate(newState);

        onSort((prevData) => {
            return {
                ...prevData,
                sortNextRebillDate: newState,
            };
        });
    };

    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.LICENSE_DELETE
    );

    const columns: ColumnDef<MigratedLicenseKeysData>[] = [
        {
            id: "Original Product",
            accessorKey: "migratedProductName",
            header: () => <span>Original Product Name</span>,
            size: 180,
            cell: ({ row }) => {
                return (
                    <RowItemCheckbox
                        accessorKey={LicenseKeysModel.user.UserName}
                        original={row.original}
                    />
                );
            },
        },
        {
            id: "Actual Product",
            accessorKey: "productName",
            header: () => <span>Actual Product Name</span>,
            size: 180,
            cell: ({ row }) => <EllipsisTooltip text={row.original.productName} />,
        },
        {
            id: "lastRebillDate",
            header: () => <span>Last Rebill date</span>,
            // ( <ButtonArrow
            //     text="Last Rebill date"
            //     state={sortNextRebillDate}
            //     onClick={() => handleSort(sortNextRebillDate)}
            // ) />
            accessorKey: "lastRebillDate",
            size: 160,
            accessorFn: ({ lastRebillDate }) => {
                if (Number(lastRebillDate) === 0) {
                    return;
                }
                return moment.unix(Number(lastRebillDate)).format("LLL");
            },
        },
        {
            id: "nextRebillDate",
            header: () => (
                <ButtonArrow
                    text="Next Rebill date"
                    state={sortNextRebillDate}
                    onClick={() => handleSort(sortNextRebillDate)}
                />
            ),
            accessorKey: "nextRebillDate",
            size: 160,
            accessorFn: ({ nextRebillDate }) => {
                if (Number(nextRebillDate) === 0) {
                    return;
                }
                return moment.unix(Number(nextRebillDate)).format("LLL");
            },
        },
        {
            id: "licenseKey",
            header: "License Key",
            accessorKey: "licenseKey",
            size: 200,
            cell: ({ row }) => (
                <CopyToClipboard text={row.original.licenseKey} />
            ),
        },
        {
            id: "globalOrderID",
            header: "Order ID",
            accessorKey: "globalOrderID",
            size: 100,
            cell: ({ row }) => <span>{row.original.globalOrderID}</span>,
        },
        {
            id: "Migrated",
            header: "Migrated",
            size: 80,
            cell: ({ row }) => (
                <MigratedStatus isMigrated={row.original.isMigrated} />
            ),
        },
        {
            id: "removed",
            header: () => null,
            size: 30,
            cell: ({ row }) => {
                return (
                    (!row.original.isMigrated || !isAccessPermission) && (
                        <RemovedPost id={row.original.id} />
                    )
                );
            },
        },
        {
            id: "expander",
            header: () => null,
            size: 30,
            cell: ({ row }) => {
                const arrow = row.getIsExpanded() ? "arrowUp" : "arrowDown";
                return (
                    <button
                        className="edit_button"
                        {...{
                            onClick: () => row.getToggleExpandedHandler(),
                            style: { cursor: "pointer" },
                        }}
                    >
                        <ImageSprite iconSprite={`${ArrowSprite}#${arrow}`} />
                    </button>
                );
            },
        },
    ];

    return (
        <Table<MigratedLicenseKeysData>
            className="migrated_license_table"
            data={dataTable}
            columns={columns}
            getRowCanExpand={() => true}
            renderSubComponent={renderSubComponent}
            enableColumnVisibility={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            renderTopToolbar={(table) => (
                <TableColumnVisibilityMenu
                    table={table}
                    onReset={handleResetColumnVisibility}
                />
            )}
        />
    );
};

export default LicenseKeysMigratedTable;
